import {
  KeyPersonRoleType,
  OwnershipBeneficialOwner,
  useEndUboRole,
  useGetRoleAssignments,
} from '@commenda/api';
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
} from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useState } from 'react';
import { getUboRemovalMessage } from '../uboRemovalMessage';

interface RemoveUboModalProps {
  onClose: () => void;
  corporationId: number;
  beneficialOwner: OwnershipBeneficialOwner;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const UBO_ROLE_KEY: KeyPersonRoleType = 'UBO';

export const RemoveUboModal = ({
  onClose,
  corporationId,
  beneficialOwner,
  onSuccess,
  onError,
}: RemoveUboModalProps) => {
  const { directorId, name } = beneficialOwner;
  const mutation = useEndUboRole(directorId);
  const { data: roleAssignments } = useGetRoleAssignments(directorId);
  const today = NonTimezoneSensitiveDate.now();
  const [removalDate, setRemovalDate] = useState<string | undefined>(() =>
    today.toISO8601String()
  );

  const roleStartDate = (roleAssignments?.items ?? []).find(
    (assignment) =>
      assignment.isActive &&
      assignment.attributeKey === UBO_ROLE_KEY &&
      assignment.corporationId === corporationId
  )?.startDate;
  const earliestRemovalDate = roleStartDate
    ? NonTimezoneSensitiveDate.fromISO8601String(roleStartDate).toDate()
    : undefined;
  // Backdating only — a future date would say "removed" while the person is still listed.
  const allowedDates = [
    ...(earliestRemovalDate ? [{ before: earliestRemovalDate }] : []),
    { after: today.toDate() },
  ];

  const handleConfirm = async () => {
    try {
      const result = await mutation.mutateAsync({ corporationId, removalDate });
      onSuccess?.(getUboRemovalMessage(name, result));
      onClose();
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Please try again.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>End UBO role for {name}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2xl px-3xl py-lg">
          <p className="txt-small-regular text-text-primary">
            {name} will stop holding a UBO role at this entity.
          </p>
          <FieldWrapper>
            <Label>Removal date</Label>
            <DatePicker
              date={removalDate}
              setDate={setRemovalDate}
              placeholder="Select date"
              disabled={allowedDates}
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" size="md" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={handleConfirm}
            disabled={!removalDate}
            isLoading={mutation.isPending}
            loadingLabel="Ending…"
          >
            End UBO role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
